import type { CreateBookingInput, RecordPaymentInput } from '../api/bookings';
import type { CashAccountChoice } from '../api/cash';
import type {
  AvailabilitySearchResult,
  AvailableUnit as AvailableUnitDto,
  CatalogueDesign,
} from '../api/masters';
import { defaultPaidFrom } from '../cash/paidFrom';
import {
  allocateParts,
  filledParts,
  setPart,
  splitParts,
  splitProblem,
  splitTotal,
  type SplitPart,
} from '../cash/split';
import { formatAmount } from '../format/money';
import { humanizeEnum } from '../format/text';

import { draftMoney, namedAccessories, type BookingDraft, type DraftGarment } from './draft';
import { priceGarment } from './pricing';
import type { AvailableUnit, HeldGarment } from './rail';

/**
 * Taking a booking — the request a draft becomes, the token that follows it, and the rail the
 * garments are picked from. One copy for the website's booking page and the phone's steps, so the two
 * cannot take a booking two different ways.
 *
 * <p>
 * Moved out of the website's `BookingsPage` when the phone needed it (2026-10-05). The rules are
 * unchanged; their arguments are the comments below, carried across.
 * </p>
 */

/** The deposit as a multiple of the rental — the counter's four, before any override. */
export const depositMultipliers: readonly number[] = [1, 1.5, 2, 3];

/** The token taken at booking, as a share of what is owed. */
export const tokenPercents: readonly number[] = [0, 25, 40, 100];

/** How a booking may start. Ordered as the conversation runs: asked, quoted, held, agreed. */
export const startStages: readonly BookingDraft['startAs'][] = [
  'Enquiry',
  'Quotation',
  'Hold',
  'Confirmed',
];

/** The `POST /api/booking` body for a draft. */
export function createBookingInput(draft: BookingDraft): CreateBookingInput {
  if (draft.customerId === null) {
    throw new Error('A booking needs a customer before it can be taken.');
  }

  return {
    customerId: draft.customerId,
    chargeFromDate: draft.chargeFrom,
    chargeToDate: draft.chargeTo,
    possessionFromDate: draft.possessionFrom,
    possessionToDate: draft.possessionTo,
    eventDate: draft.eventDate || null,
    eventKind: draft.eventKind || null,
    stage: draft.startAs,
    depositMultiplier: draft.depositOverride === null ? draft.depositMultiplier : null,
    depositOverride: draft.depositOverride,
    depositOverrideReason: draft.depositOverrideReason || null,
    depositCollectionPoint: draft.depositCollectionPoint,
    depositRefundPoint: draft.depositRefundPoint,

    // Whichever way it was typed. Both null is no discount, and the server reads that as none
    // rather than as zero — so a booking with no concession carries no discount columns at all.
    discountPct: draft.discountPct,
    discountAmount: draft.discountAmount,
    discountReason: draft.discountReason || null,
    notes: draft.notes || null,

    // Only on a hold, and required there. The server refuses it on any other stage rather than
    // quietly ignoring it, so sending it unconditionally would break every other start.
    holdHours: draft.startAs === 'Hold' ? draft.holdHours : null,
    wearers: draft.wearers.map((wearer) => ({
      name: wearer.name,
      relation: wearer.relation || null,
      mobile: wearer.mobile || null,
      customerId: wearer.customerId,
      fittingAppointmentOn: wearer.fittingAppointmentOn || null,
      garments: wearer.garments.map((garment) => ({
        unitId: garment.unitId,

        // Each line's own concession. The server resolves it against that garment's own rate and
        // the ceiling is checked against these plus the booking share together.
        discountPct: garment.discountPct,
        discountAmount: garment.discountAmount,

        // What goes out attached to it, so the return counter has something to check against.
        // Named rows only: a blank one is the editor's own artefact and the server would refuse the
        // whole booking for it.
        accessories: namedAccessories(garment).map((accessory) => ({
          name: accessory.name.trim(),
          quantity: accessory.quantity,
          replacementCharge: accessory.replacementCharge,
        })),
      })),
    })),
  };
}

/**
 * The token to record straight after the booking is created — a payment of its own, because
 * `POST /api/booking` deliberately carries no money: a booking exists whether or not anything was
 * paid. **Only a confirmed booking takes money at the counter**; an enquiry or quotation has agreed
 * nothing yet, and a hold that lapses must not leave a receipt behind it. The same arithmetic the
 * confirm button showed (`draftMoney`), so the button and the receipt cannot drift apart.
 */
export function tokenToTake(draft: BookingDraft): number {
  return draft.startAs === 'Confirmed' ? Math.max(0, draftMoney(draft).token) : 0;
}

/**
 * The deposit taken the moment a booking is confirmed: all of it when the shop takes it at
 * booking, and none for anything but a confirmed booking, for {@link tokenToTake}'s reason.
 *
 * <p>
 * <strong>A split is not halved here, deliberately.</strong> The server holds a deposit whole or
 * not at all — the first deposit payment marks the whole amount held — so half taken at booking
 * would be refunded in full at return. Until the server counts what was actually paid in, a split
 * is taken at pickup, as it was before.
 * </p>
 */
export function depositAtBooking(draft: BookingDraft): number {
  if (draft.startAs !== 'Confirmed' || draft.depositCollectionPoint !== 'AtBooking') {
    return 0;
  }

  return Math.max(0, draftMoney(draft).deposit);
}

/**
 * Everything the counter takes when the booking is confirmed, **in one press** (owner, 2026-10-14):
 * the token and, where the shop takes it then, the deposit — one way of paying for both.
 *
 * <p>
 * <strong>Still two payments, never one.</strong> The token is part of the rental, an invoice's
 * money; the deposit is not a supply and is held, not earned (rule 5). One row for both would put
 * the deposit inside the rental and tax it. So the counter sends two, under one method, and prints
 * them on one receipt.
 * </p>
 */
export function paymentsAtBooking(draft: BookingDraft): RecordPaymentInput[] {
  const token = tokenToTake(draft);
  const deposit = depositAtBooking(draft);
  const parts = filledParts(draft.payParts ?? []);

  if (parts.length > 0) {
    return paymentsFromParts(token, deposit, parts);
  }

  return [
    ...(token > 0 ? [{ kind: 'Token' as const, method: draft.payMethod, amount: token }] : []),
    ...(deposit > 0
      ? [{ kind: 'Deposit' as const, method: draft.payMethod, amount: deposit }]
      : []),
  ];
}

/**
 * The bag's account boxes as payments (split payments step 6, owner 2026-11-01): the token first,
 * then the deposit, out of the boxes in the order shown — ₹4,000 in the till and ₹3,000 by UPI
 * against a ₹2,000 token and ₹5,000 deposit is a ₹2,000 cash token, a ₹2,000 cash deposit part and
 * a ₹3,000 UPI deposit part, each naming its account. Still never one row for both kinds.
 */
export function paymentsFromParts(
  token: number,
  deposit: number,
  parts: readonly SplitPart[],
): RecordPaymentInput[] {
  return allocateParts(
    [
      { kind: 'Token' as const, amount: token },
      { kind: 'Deposit' as const, amount: deposit },
    ],
    parts,
  ).map(({ kind, part, amount }) => ({
    kind,
    method: part.method,
    amount,
    reference: part.reference.trim() || null,
    cashAccountId: part.accountId,
  }));
}

/** What the bag takes now: the token and whatever part of the deposit is taken at booking. */
export function bagTakenNow(draft: BookingDraft): number {
  return tokenToTake(draft) + depositAtBooking(draft);
}

/**
 * The bag's account boxes, as the "Taken now" card shows them (split payments; owner, 2026-11-01:
 * on the bag itself, not behind the token). Untouched, all of it sits in the default account for the
 * bag's method — the till, for cash — which is exactly what is sent while nothing is typed; once
 * typed, the draft's own boxes.
 */
export function bagBoxes(
  draft: BookingDraft,
  accounts: readonly CashAccountChoice[] | undefined,
): SplitPart[] {
  if (draft.payParts) {
    return draft.payParts;
  }

  const fresh = splitParts(accounts);
  const takenNow = bagTakenNow(draft);
  const target = defaultPaidFrom(draft.payMethod, accounts);

  return takenNow > 0 && target !== null
    ? setPart(fresh, target, { amount: String(takenNow) })
    : fresh;
}

/**
 * What stops the bag confirming with its money split: the boxes must come to what is taken now — no
 * part of the token or deposit left unplaced, nothing taken beyond it. Null when untouched or equal.
 */
export function bagPartsProblem(draft: BookingDraft, currency = ''): string | null {
  const parts = draft.payParts ?? [];

  if (filledParts(parts).length === 0) {
    return null;
  }

  const boxes = splitProblem(parts, 'into');

  if (boxes) {
    return boxes;
  }

  const takenNow = bagTakenNow(draft);
  const placed = splitTotal(parts);

  return Math.abs(placed - takenNow) < 0.005
    ? null
    : `The accounts come to ${currency}${formatAmount(placed)}; ${currency}${formatAmount(takenNow)} is taken now.`;
}

/** The confirm button, naming all the money it takes: "Confirm and take ₹7,000". */
export function confirmLabel(draft: BookingDraft, currency = ''): string {
  const total = paymentsAtBooking(draft).reduce((sum, payment) => sum + payment.amount, 0);

  return total > 0 ? `Confirm and take ${currency}${formatAmount(total)}` : 'Confirm the booking';
}

/**
 * The rail: one availability answer per design, flattened into the picker's own shape. Each
 * garment carries its own rate card (resolved server-side through the whole chain), and a garment
 * that is not free is kept, with what is in the way — "no, but it is back on the 15th" is what a
 * clerk needs, and an empty list sends the customer elsewhere.
 */
export function railUnits(
  results: readonly Pick<AvailabilitySearchResult, 'itemId' | 'itemName' | 'units'>[],
  categoryOf: ReadonlyMap<number, { id: number | null; name: string }>,
): AvailableUnit[] {
  return results.flatMap((result) =>
    result.units.map((unit): AvailableUnit => ({
      unitId: unit.unitId,
      serialCode: unit.serialCode,
      referenceNumber: unit.referenceNumber ?? null,
      itemId: result.itemId,
      itemName: result.itemName,
      categoryId: categoryOf.get(result.itemId)?.id ?? null,
      categoryName: categoryOf.get(result.itemId)?.name ?? '',
      variantDisplayName: unit.variantDisplayName,
      rateCard: unit.rateCard,
      imageUrl: unit.imageUrl,
      heldBy: unit.free ? null : whatIsInTheWay(unit),
    })),
  );
}

/**
 * The rail from the one-call catalogue (`GET /api/availability/catalogue`) — the same shape as
 * {@link railUnits}, so the garment view, the scan and the draft read it unchanged. The category
 * comes with each design here, where the per-design search had to have it joined on.
 */
export function railFromCatalogue(designs: readonly CatalogueDesign[]): AvailableUnit[] {
  return railUnits(
    designs,
    new Map(
      designs.map((design) => [
        design.itemId,
        { id: design.categoryId, name: design.categoryName },
      ]),
    ),
  );
}

/**
 * The one thing to say about a garment that is not free. **The row with a party behind it wins**,
 * because that is the one a clerk can act on — a name and a number can be asked whether they still
 * want it, while "cleaning until Tuesday" is an answer. The buffer is named as the buffer rather
 * than as a longer booking, for the reason it is a separate row at all.
 */
export function whatIsInTheWay(unit: AvailableUnitDto): HeldGarment {
  const held = unit.blockedBy.find((block) => block.heldBy !== null) ?? unit.blockedBy[0];

  return {
    tentative: held?.isTentative ?? false,
    fromDate: held?.fromDate ?? '',
    untilDate: held?.toDate ?? '',
    freeOn: unit.nextFreeFrom ?? '',
    holder: held?.heldBy ?? null,
    reason: held?.isBuffer ? 'Cleaning buffer' : humanizeEnum(held?.blockType ?? 'Booking'),
  };
}

/**
 * A garment picked off the rail, as a line of the draft — at card price for the charged days, with
 * its own rate card carried so the draft re-prices *this* garment when the dates move.
 *
 * <p>
 * No concession until somebody gives one (a picked garment at card price, never a ₹0 discount line on
 * the quote), and nothing attached until the clerk says so — the plain suit is the common case.
 * </p>
 */
export function garmentFromUnit(unit: AvailableUnit, days: number): DraftGarment {
  return {
    unitId: unit.unitId,
    serialCode: unit.serialCode,
    ...(unit.referenceNumber ? { referenceNumber: unit.referenceNumber } : {}),
    itemId: unit.itemId,
    itemName: `${unit.itemName} · ${unit.variantDisplayName}`,
    rate: priceGarment(unit.rateCard, days)?.amount ?? null,
    rateCard: unit.rateCard,
    discountPct: null,
    discountAmount: null,
    accessories: [],
    designName: unit.itemName,
    imageUrl: unit.imageUrl ?? null,
    categoryName: unit.categoryName,
  };
}

/**
 * Every line re-priced for `days`, from each garment's own card. **Without this the money silently
 * goes stale**: three garments picked for a three-day hire, the customer then keeps them a week, and
 * the summary would show the three-day price while the server charged the seven-day one. Returns the
 * same draft when nothing moved, so a caller setting state from it does not render forever.
 */
export function repriceDraft(draft: BookingDraft, days: number): BookingDraft {
  let moved = false;

  const wearers = draft.wearers.map((wearer) => ({
    ...wearer,
    garments: wearer.garments.map((garment) => {
      const rate = priceGarment(garment.rateCard, days)?.amount ?? null;

      if (rate === garment.rate) {
        return garment;
      }

      moved = true;

      return { ...garment, rate };
    }),
  }));

  return moved ? { ...draft, wearers } : draft;
}

/**
 * The counter's multiples with the shop's own among them, in order. A shop set to 1.25× would
 * otherwise start every booking on a multiple no chip names, and lose it the moment another is tapped.
 */
export function depositMultiplierChoices(shop: number): readonly number[] {
  return depositMultipliers.includes(shop)
    ? depositMultipliers
    : [...depositMultipliers, shop].sort((a, b) => a - b);
}
